/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import * as ml from '../../ml_parser/ast';
import {XmlParser} from '../../ml_parser/xml_parser';
import * as i18n from '../i18n_ast';
import {I18nError} from '../parse_util';

import {Serializer} from './serializer';
import {digest} from './xmb';

const /** @type {?} */ _TRANSLATIONS_TAG = 'translationbundle';
const /** @type {?} */ _TRANSLATION_TAG = 'translation';
const /** @type {?} */ _PLACEHOLDER_TAG = 'ph';

export class Xtb implements Serializer {
/**
 * @param {?} messages
 * @return {?}
 */
write(messages: i18n.Message[]): string { throw new Error('Unsupported'); }
/**
 * @param {?} content
 * @param {?} url
 * @return {?}
 */
load(content: string, url: string): {[msgId: string]: i18n.Node[]} {
    // xtb to xml nodes
    const /** @type {?} */ xtbParser = new XtbParser();
    const {mlNodesByMsgId, errors} = xtbParser.parse(content, url);

    // xml nodes to i18n nodes
    const /** @type {?} */ i18nNodesByMsgId: {[msgId: string]: i18n.Node[]} = {};
    const /** @type {?} */ converter = new XmlToI18n();
    Object.keys(mlNodesByMsgId).forEach(msgId => {
      const {i18nNodes, errors: e} = converter.convert(mlNodesByMsgId[msgId]);
      errors.push(...e);
      i18nNodesByMsgId[msgId] = i18nNodes;
    });

    if (errors.length) {
      throw new Error(`xtb parse errors:\n${errors.join('\n')}`);
    }

    return i18nNodesByMsgId;
  }
/**
 * @param {?} message
 * @return {?}
 */
digest(message: i18n.Message): string { return digest(message); }
}

// Extract messages as xml nodes from the xtb file
class XtbParser implements ml.Visitor {
private _bundleDepth: number;
private _errors: I18nError[];
private _mlNodesByMsgId: {[msgId: string]: ml.Node[]};
/**
 * @param {?} xtb
 * @param {?} url
 * @return {?}
 */
parse(xtb: string, url: string) {
    this._bundleDepth = 0;
    this._mlNodesByMsgId = {};

    const /** @type {?} */ xml = new XmlParser().parse(xtb, url, true);

    this._errors = xml.errors;
    ml.visitAll(this, xml.rootNodes);

    return {
      mlNodesByMsgId: this._mlNodesByMsgId,
      errors: this._errors,
    };
  }
/**
 * @param {?} element
 * @param {?} context
 * @return {?}
 */
visitElement(element: ml.Element, context: any): any {
    switch (element.name) {
      case _TRANSLATIONS_TAG:
        this._bundleDepth++;
        if (this._bundleDepth > 1) {
          this._addError(element, `<${_TRANSLATIONS_TAG}> elements can not be nested`);
        }
        ml.visitAll(this, element.children, null);
        this._bundleDepth--;
        break;

      case _TRANSLATION_TAG:
        const /** @type {?} */ idAttr = element.attrs.find((attr) => attr.name === 'id');
        if (!idAttr) {
          this._addError(element, `<${_TRANSLATION_TAG}> misses the "id" attribute`);
        } else {
          const /** @type {?} */ id = idAttr.value;
          if (this._mlNodesByMsgId.hasOwnProperty(id)) {
            this._addError(element, `Duplicated translations for msg ${id}`);
          } else {
            this._mlNodesByMsgId[id] = element.children;
          }
        }
        break;

      default:
        this._addError(element, 'Unexpected tag');
    }
  }
/**
 * @param {?} attribute
 * @param {?} context
 * @return {?}
 */
visitAttribute(attribute: ml.Attribute, context: any): any {}
/**
 * @param {?} text
 * @param {?} context
 * @return {?}
 */
visitText(text: ml.Text, context: any): any {}
/**
 * @param {?} comment
 * @param {?} context
 * @return {?}
 */
visitComment(comment: ml.Comment, context: any): any {}
/**
 * @param {?} expansion
 * @param {?} context
 * @return {?}
 */
visitExpansion(expansion: ml.Expansion, context: any): any {}
/**
 * @param {?} expansionCase
 * @param {?} context
 * @return {?}
 */
visitExpansionCase(expansionCase: ml.ExpansionCase, context: any): any {}
/**
 * @param {?} node
 * @param {?} message
 * @return {?}
 */
private _addError(node: ml.Node, message: string): void {
    this._errors.push(new I18nError(node.sourceSpan, message));
  }
}

function XtbParser_tsickle_Closure_declarations() {
 /** @type {?} */
XtbParser.prototype._bundleDepth;
 /** @type {?} */
XtbParser.prototype._errors;
 /** @type {?} */
XtbParser.prototype._mlNodesByMsgId;
}


// Convert ml nodes (xtb syntax) to i18n nodes
class XmlToI18n implements ml.Visitor {
private _errors: I18nError[];
/**
 * @param {?} nodes
 * @return {?}
 */
convert(nodes: ml.Node[]) {
    this._errors = [];
    return {
      i18nNodes: ml.visitAll(this, nodes),
      errors: this._errors,
    };
  }
/**
 * @param {?} text
 * @param {?} context
 * @return {?}
 */
visitText(text: ml.Text, context: any) { return new i18n.Text(text.value, text.sourceSpan); }
/**
 * @param {?} icu
 * @param {?} context
 * @return {?}
 */
visitExpansion(icu: ml.Expansion, context: any) {
    const /** @type {?} */ caseMap: {[value: string]: i18n.Node} = {};

    ml.visitAll(this, icu.cases).forEach(c => {
      caseMap[c.value] = new i18n.Container(c.nodes, icu.sourceSpan);
    });

    return new i18n.Icu(icu.switchValue, icu.type, caseMap, icu.sourceSpan);
  }
/**
 * @param {?} icuCase
 * @param {?} context
 * @return {?}
 */
visitExpansionCase(icuCase: ml.ExpansionCase, context: any): any {
    return {
      value: icuCase.value,
      nodes: ml.visitAll(this, icuCase.expression),
    };
  }
/**
 * @param {?} el
 * @param {?} context
 * @return {?}
 */
visitElement(el: ml.Element, context: any): i18n.Placeholder {
    if (el.name === _PLACEHOLDER_TAG) {
      const /** @type {?} */ nameAttr = el.attrs.find((attr) => attr.name === 'name');
      if (nameAttr) {
        return new i18n.Placeholder('', nameAttr.value, el.sourceSpan);
      }

      this._addError(el, `<${_PLACEHOLDER_TAG}> misses the "name" attribute`);
    } else {
      this._addError(el, `Unexpected tag`);
    }
  }
/**
 * @param {?} comment
 * @param {?} context
 * @return {?}
 */
visitComment(comment: ml.Comment, context: any) {}
/**
 * @param {?} attribute
 * @param {?} context
 * @return {?}
 */
visitAttribute(attribute: ml.Attribute, context: any) {}
/**
 * @param {?} node
 * @param {?} message
 * @return {?}
 */
private _addError(node: ml.Node, message: string): void {
    this._errors.push(new I18nError(node.sourceSpan, message));
  }
}

function XmlToI18n_tsickle_Closure_declarations() {
 /** @type {?} */
XmlToI18n.prototype._errors;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
